import { readNwiAcreageQualification } from './nwiAcreageQualification';
import { screenCriticalHabitat } from './criticalHabitatScreen';
/**
 * USFWS Critical Habitat + NWI Wetlands — environmental constraints.
 * Free, no API key, ArcGIS REST queries.
 *
 * CA3: queries at the boundary centroid when a polygon is drawn.
 * CA4: when a boundary polygon is in scope, the queries widen to the parcel
 * bounding box + a 500 m buffer (`esriGeometryEnvelope`) so off-parcel-but-
 * implicating habitat / wetlands — habitat ranges and wetland polygons that
 * extend just beyond the parcel line — are caught too. Point-only legacy
 * sites keep the single-point intersect query at the centroid.
 *
 * RD-1 (C2): each screen is LOCAL-FIRST — the nationwide refdata snapshots
 * (`usfws-critical-habitat`, `nwi-wetlands`) answer the same point/envelope
 * intersect from GCS; a per-dataset miss/error falls through to the live
 * ArcGIS query unchanged. Aggregation (species dedupe, wetland-type dedupe,
 * 1-decimal acreage canonicalisation) is SHARED between the two sources so
 * `sed.environmental.data.*` grounding paths stay bit-stable.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, CACHE_TTL } from './types';
import { queryArcgisRest } from '../arcgis/queryArcgisRest';
import { refdataContains, refdataIntersect, tryRefdata, isPrivateRefdataAcceptanceDataset } from '../refdata/refdataClient';
import type { NwiMappingCoverage, RefdataDatasetId, RefdataLatLng, RefdataResultMeta } from '../../types/refdata';
import { nwiGroupFromSourceFeatureId } from './nwiSourceIdentity';
import { nwiMappingCoverage } from './nwiMappingCoverage';

export interface EnvironmentalData {
  /** Mapped final OR proposed habitat; undefined means the source screen is incomplete. */
  hasCriticalHabitat?: boolean;
  criticalHabitatFindings?: import('../../types/refdata').CriticalHabitatFinding[];
  criticalHabitatCoverage?: import('../../types/refdata').CriticalHabitatCoverage;
  species: string[];
  hasWetlands?: boolean;
  wetlandTypes: string[];
  wetlandAcreage?: number;
  wetlandAcreageBasis?: 'source-feature-total';
  wetlandSourceAcres?: Record<string, number>;
  wetlandSourceAcreageConflicts?: import('../../types/refdata').NwiSourceAcreageConflict[];
  wetlandAcreageIssues?: import('../../types/refdata').NwiAcreageIssue[];
  wetlandMappingCoverage?: NwiMappingCoverage;
  /** CA4 — true when the queries used the buffered-envelope sweep
   *  (a drawn boundary was in scope) rather than a single point. */
  bufferedQuery?: boolean;
}

/** ~metres-per-degree of latitude (constant) and longitude (cos-scaled). */
function bufferDegrees(bufferMeters: number, atLat: number): { dLat: number; dLng: number } {
  const dLat = bufferMeters / 111_320;
  const dLng = bufferMeters / (111_320 * Math.max(0.01, Math.cos((atLat * Math.PI) / 180)));
  return { dLat, dLng };
}

interface ParcelEnvelope {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

/** Parcel bbox + buffer — the SAME envelope both the live esriGeometryEnvelope
 *  query and the refdata intersect polygon are built from. */
function bufferedEnvelope(
  boundary: { lat: number; lng: number }[],
  bufferMeters: number,
): ParcelEnvelope {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of boundary) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  const { dLat, dLng } = bufferDegrees(bufferMeters, (minLat + maxLat) / 2);
  return { xmin: minLng - dLng, ymin: minLat - dLat, xmax: maxLng + dLng, ymax: maxLat + dLat };
}

/** Build an ArcGIS query string — either a point intersect or a buffered
 *  envelope around the parcel bounding box. */
function buildGeometryParams(
  lat: number,
  lng: number,
  envelope: ParcelEnvelope | null,
): string {
  if (!envelope) {
    return `geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326`;
  }
  return `geometry=${envelope.xmin},${envelope.ymin},${envelope.xmax},${envelope.ymax}&geometryType=esriGeometryEnvelope&inSR=4326`;
}

function envelopePolygon(envelope: ParcelEnvelope): RefdataLatLng[] {
  return [
    { lat: envelope.ymin, lng: envelope.xmin },
    { lat: envelope.ymin, lng: envelope.xmax },
    { lat: envelope.ymax, lng: envelope.xmax },
    { lat: envelope.ymax, lng: envelope.xmin },
  ];
}

interface RefdataScreen {
  attrs: Array<Record<string, string | number | null>>;
  vintage: string;
  meta: RefdataResultMeta;
  truncated: boolean;
}

/**
 * Cache contract version. `withCache` reads Firestore BEFORE provider.fetch,
 * and the cache key encodes only LOCATION — so a change in what produces the
 * answer is invisible to it and stale entries shadow refdata indefinitely.
 *
 * Redhead's SED v34 is the proof: `wetlandAcres: 65.7`, source "... (cached)",
 * served from an entry written before the refdata swap, while live refdata at
 * the same coordinates answers from the 0.1-degree snapshot. hifldGrid needed
 * exactly this bump (v2-refdata-snapshot) and got one; the other seven
 * refdata-backed providers did not, which made the whole migration invisible
 * to every existing site until TTL expiry.
 *
 * Bump this string whenever the DATA SOURCE or its shape changes.
 */
const USFWS_HABITAT_CACHE_CONTRACT_VERSION = 'v7-nwi-source-acreage-qualification';

export const environmentalProvider: DataProvider<EnvironmentalData> = {
  id: 'usfws-env',
  name: 'USFWS Critical Habitat & NWI Wetlands',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${USFWS_HABITAT_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<EnvironmentalData>> {
    const now = new Date().toISOString();
    const boundary = geo?.boundary && geo.boundary.length >= 3 ? geo.boundary : undefined;
    const BUFFER_M = 500;
    const envelope = boundary ? bufferedEnvelope(boundary, BUFFER_M) : null;
    const geomParams = buildGeometryParams(lat, lng, envelope);
    const queryNote = boundary
      ? ` (parcel bounding box + ${BUFFER_M}m buffer)`
      : '';

    // RD-1 — refdata screen shared by both datasets: envelope intersect when
    // a boundary is in scope (mirrors the live esriGeometryEnvelope query),
    // else point containment (mirrors the live esriGeometryPoint intersect).
    const refdataScreen = async (datasetId: RefdataDatasetId, limit: number): Promise<RefdataScreen | null> => {
      if (envelope) {
        const res = await refdataIntersect(datasetId, envelopePolygon(envelope), { limit });
        return res ? { attrs: res.hits.map(h => ({ ...h.feature.props, __sourceId: h.feature.id })), vintage: res.vintageDate, meta: res, truncated: res.truncated !== false } : null;
      }
      const res = await refdataContains(datasetId, { lat, lng });
      return res ? { attrs: res.features.slice(0, limit).map(f => ({ ...f.props, __sourceId: f.id })), vintage: res.vintageDate, meta: res, truncated: res.truncated !== false || res.features.length > limit } : null;
    };

    const localCh = await tryRefdata('usfws-critical-habitat', () => refdataScreen('usfws-critical-habitat', 1000));
    const { detail: habitatDetail, ...habitat } = await screenCriticalHabitat(localCh, geomParams,
      boundary ? 'buffered-envelope' : 'point', geo?.signal);

    // 2. Query NWI Wetlands
    //
    // PRR-3 — host migrated. The legacy `fwsprimary.wim.usgs.gov` MapServer
    // has been intermittently dead in prod; USGS publishes the canonical
    // NWI MapServer at `fwspublicservices.wim.usgs.gov/wetlandsmapservice/`.
    // Both hosts remain in the proxy allowlist (`server.ts`) so a future
    // flip back is one-line.
    //
    // PRR-3 (2026-05-21): `fwspublicservices` MapServer requires the joined
    // field prefix `Wetlands.*` — bare `WETLAND_TYPE` returns HTTP 200 with
    // `{error: {message: "Failed to execute query"}}` (misread as outage).
    // Preserve independent habitat findings when NWI fails; never stamp absence.
    // (Refdata cells carry BARE keys — the ingest strips the join prefix —
    // and wetlandTypeOf/wetlandAcresOf below accept both spellings.)
    const localNwi = await tryRefdata('nwi-wetlands', () => refdataScreen('nwi-wetlands', 2000));
    let nwiAttrs: Array<Record<string, unknown>>;
    let nwiSnapshotNote = '';
    let truncated = false;
    let nwiFailure: string | undefined;
    try {
    if (localNwi) {
      nwiAttrs = localNwi.attrs;
      truncated = localNwi.truncated;
      nwiSnapshotNote = ` [local snapshot, vintage ${localNwi.vintage}]`;
    } else {
      if (isPrivateRefdataAcceptanceDataset('nwi-wetlands')) throw new Error('Private NWI acceptance snapshot unavailable; live supplementation is withheld');
      const nwiUrl = `https://fwspublicservices.wim.usgs.gov/wetlandsmapservice/rest/services/Wetlands/MapServer/0/query?` +
        `${geomParams}&spatialRel=esriSpatialRelIntersects&outFields=Wetlands.WETLAND_TYPE,Wetlands.ACRES&resultRecordCount=50&f=json`;
      const nwiJson = await queryArcgisRest(nwiUrl, {
        label: 'NWI Wetlands',
        signal: geo?.signal,
      });
      if (!Array.isArray(nwiJson?.features)) throw new Error('NWI Wetlands returned malformed features');
      truncated = nwiJson.exceededTransferLimit === true || nwiJson.features.length >= 50;
      nwiAttrs = nwiJson.features.map((f: { attributes?: Record<string, unknown> }) => f.attributes ?? {});
    }
    } catch (error) {
      geo?.signal?.throwIfAborted();
      nwiFailure = error instanceof Error ? error.message : String(error);
      nwiAttrs = [];
      if (habitat.criticalHabitatCoverage.layers.every(layer => layer.status === 'unavailable') && !habitat.hasCriticalHabitat) {
        throw new Error(habitat.criticalHabitatCoverage.layers[0]?.reason ?? nwiFailure);
      }
    }
    const mapping = await nwiMappingCoverage(localNwi?.meta ?? null, { point: { lat, lng }, ...(envelope ? { polygon: envelopePolygon(envelope) } : {}) });
    const wetlandMappingCoverage: NwiMappingCoverage = nwiFailure
      ? { status: 'unknown', reason: `NWI source unavailable: ${nwiFailure}` } : truncated
      ? { status: 'unknown', reason: 'Wetland feature query was truncated; mapped findings are incomplete.' } : mapping;
    const hasWetlands = nwiAttrs.length > 0 ? true : wetlandMappingCoverage.status === 'complete' ? false : undefined;
    const wetlandTypeOf = (attrs: Record<string, unknown> | undefined): string | undefined =>
      (attrs?.['Wetlands.WETLAND_TYPE'] ?? attrs?.WETLAND_TYPE) as string | undefined;
    const wetlandAcresOf = (attrs: Record<string, unknown> | undefined): number | undefined =>
      (attrs?.['Wetlands.ACRES'] ?? attrs?.ACRES) as number | undefined;
    const wetlandTypes = [...new Set(nwiAttrs.map((attrs) => wetlandTypeOf(attrs)).filter(Boolean))] as string[];
    // ACRES describes the full upstream wetland feature, not the query or parcel
    // intersection. Cell/multipart fragments repeat it; dedupe by the compiler's full source geometry identity.
    // NWI_ID alone is not unique: disjoint Arizona polygons share one ID.
    const wetlandSourceAcres: Record<string, number> = {};
    const conflicts = new Map<string, NonNullable<EnvironmentalData['wetlandSourceAcreageConflicts']>[number]>();
    const issues = new Map<string, NonNullable<EnvironmentalData['wetlandAcreageIssues']>[number]>();
    const excludedSourceIds = new Set<string>();
    let acreageValid = true;
    let ambiguousSourceGroups = false;
    const groupSources = new Map<string,string>();
    let sourceIdentitiesComplete = true;
    nwiAttrs.forEach((attrs, index) => {
      const identity = attrs.sourceFeatureId ?? attrs.__sourceId ?? attrs['Wetlands.OBJECTID'] ?? attrs.OBJECTID;
      if (identity == null) sourceIdentitiesComplete = false;
      const sourceId = String(identity ?? `live-row-${index}`);
      const group = attrs.NWI_ID ?? attrs['Wetlands.NWI_ID'] ?? nwiGroupFromSourceFeatureId(sourceId);
      if (group != null && String(group).trim()) {
        const prior = groupSources.get(String(group));
        if (prior && prior !== sourceId) ambiguousSourceGroups = true;
        groupSources.set(String(group),sourceId);
      }
      const qualification = readNwiAcreageQualification(attrs);
      if (qualification.status !== 'ordinary') {
        acreageValid = false;
        excludedSourceIds.add(sourceId);
        delete wetlandSourceAcres[sourceId];
        if (qualification.status === 'qualified-conflict') {
          const value = qualification.conflict;
          conflicts.set(`${value.sourceFeatureId}:${value.sourceQualificationReceiptHash}`, value);
        } else issues.set(sourceId, { sourceFeatureId: sourceId, reason: qualification.reason });
        return;
      }
      if (excludedSourceIds.has(sourceId)) return;
      const acres = wetlandAcresOf(attrs);
      if (typeof acres !== 'number' || !Number.isFinite(acres) || acres < 0) { acreageValid = false; return; }
      if (wetlandSourceAcres[sourceId] !== undefined && wetlandSourceAcres[sourceId] !== acres) {
        acreageValid = false;
        excludedSourceIds.add(sourceId);
        delete wetlandSourceAcres[sourceId];
        issues.set(sourceId, { sourceFeatureId: sourceId, reason: 'The same wetland source identity returned different acreage values without a complete source qualification. Acreage remains unavailable.' });
        return;
      }
      wetlandSourceAcres[sourceId] = acres;
    });
    const wetlandAcreage = acreageValid && !ambiguousSourceGroups && (hasWetlands === true || hasWetlands === false)
      ? Math.round(Object.values(wetlandSourceAcres).reduce((sum, acres) => sum + acres, 0) * 10) / 10 : undefined;
    const wetlandsDetail = hasWetlands
      ? `${wetlandTypes.join(', ')}${wetlandAcreage === undefined ? (conflicts.size ? ' (acreage unavailable: source exports disagree; exact original values retained)' : ambiguousSourceGroups ? ' (acreage unresolved: multiple source geometries share an NWI group and may overlap)' : ' (acreage unavailable)') : ` (${wetlandAcreage.toFixed(1)} acres, full source-feature totals; not acreage within the parcel)`}${queryNote}${nwiSnapshotNote}. ${wetlandMappingCoverage.reason}`
      : `${hasWetlands === false ? 'No mapped wetlands in the covered query' : 'Wetlands absence unresolved'}${queryNote || ' at this location'}${nwiSnapshotNote}. ${wetlandMappingCoverage.reason}`;

    // Content-Source Fixes Prompt 11 — emit ONE composite row for the
    // entire environmental provenance. Previously two rows
    // (`environmental.criticalHabitat`, `environmental.wetlands`)
    // doubled the appendix's environmental footprint; with the
    // dedup-by-field at render time gone (Prompt 4) both would
    // survive. Composite with `subItems` is the right shape.
    const sources: ProviderResult<EnvironmentalData>['sources'] = [{
      field: 'environmental',
      source: boundary
        ? `USFWS Critical Habitat + NWI Wetlands Inventory — parcel bounding box + ${BUFFER_M}m buffer`
        : 'USFWS Critical Habitat + NWI Wetlands Inventory',
      url: 'https://www.fws.gov/program/national-wetlands-inventory',
      retrievedAt: now,
      subItems: [
        { topic: 'Critical habitat', detail: habitatDetail },
        { topic: 'Wetlands', detail: wetlandsDetail },
      ],
    }];

    return {
      data: { ...habitat, hasWetlands, wetlandTypes, wetlandAcreage,
        wetlandAcreageBasis: 'source-feature-total', wetlandSourceAcres: sourceIdentitiesComplete ? wetlandSourceAcres : undefined, wetlandMappingCoverage, bufferedQuery: !!boundary,
        ...(conflicts.size ? { wetlandSourceAcreageConflicts: [...conflicts.values()] } : {}),
        ...(issues.size ? { wetlandAcreageIssues: [...issues.values()] } : {}),
      },
      sources,
      ...(habitat.criticalHabitatCoverage.status !== 'complete' || nwiFailure || truncated ? { cacheTtlMs: 0 } : {}),
    };
  },
};
